import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {PipelineStepType, PipelineType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';

import {UseStepTypeInfosQuery} from './__generated/UseStepTypeInfosQuery';

export const USE_STEP_TYPE_INFOS_QUERY = gql`
  query UseStepTypeInfosQuery @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    automationStepTypeInfos {
      stepType
      userFacingName
      icon
      description
      tags
      groups
      configFlags
      requiredCapabilityManagementFeatures
      blockedPipelineTypes
      userCanCreateDirectly
      isClonable
      isCopyable
    }
  }
`;

export interface StepTypeInfo {
  userFacingName: string;
  icon: string | null;
  description: string | null;
  tags: Array<string>;
  groups: Array<string>;
  configFlags: Array<string>;
  requiredCapabilityManagementFeatures: Array<string>;
  blockedPipelineTypes: Array<PipelineType>;
  userCanCreateDirectly: boolean;
  isClonable: boolean;
  isCopyable: boolean;
}

function toStepTypeInfoMap(
  infos: UseStepTypeInfosQuery['automationStepTypeInfos'] | undefined
): Map<PipelineStepType, StepTypeInfo> {
  if (infos === undefined) {
    return new Map();
  }
  return new Map(
    infos.map((info) => [
      info.stepType,
      {
        userFacingName: info.userFacingName,
        icon: info.icon,
        description: info.description,
        tags: info.tags,
        groups: info.groups,
        configFlags: info.configFlags,
        requiredCapabilityManagementFeatures: info.requiredCapabilityManagementFeatures,
        blockedPipelineTypes: info.blockedPipelineTypes,
        userCanCreateDirectly: info.userCanCreateDirectly,
        isClonable: info.isClonable,
        isCopyable: info.isCopyable,
      },
    ])
  );
}

/**
 * Fetches per-step-type presentation and gating metadata (display name, icon, description, tags,
 * groups, gating flags/features, clone/copy eligibility) from the Domain Graph
 * (automationStepTypeInfos) so the frontend reads it at runtime rather than from a hardcoded
 * build-time map. Returns a lookup keyed by step type; the map is empty while the query is loading.
 */
export default function useStepTypeInfos(): Map<PipelineStepType, StepTypeInfo> {
  const result = useBenchlingQuery<UseStepTypeInfosQuery>(USE_STEP_TYPE_INFOS_QUERY);
  const infos = result.type === QueryResultType.SUCCESS ? result.data.automationStepTypeInfos : undefined;
  return useMemo(() => toStepTypeInfoMap(infos), [infos]);
}

/**
 * Reads step-type infos from the Apollo cache without a React hook, for imperative (non-component)
 * callers such as flowchart action functions. Returns an empty map if the query has not been fetched
 * yet; components that render step-type metadata drive the fetch via `useStepTypeInfos`.
 */
export function readStepTypeInfosFromCache(): Map<PipelineStepType, StepTypeInfo> {
  const data = getApolloClient().readQuery<UseStepTypeInfosQuery>({query: USE_STEP_TYPE_INFOS_QUERY});
  return toStepTypeInfoMap(data?.automationStepTypeInfos ?? undefined);
}
